import { useEffect, useState } from "react";

/**
 * Photos with a transparent background (cut-out products) vanish on a white card. This looks at the photo once,
 * and when it is transparent returns a backdrop that contrasts with the product's own colour. Opaque photos return null,
 * so they display exactly as before. Results are cached per URL.
 */
const cache = new Map();   // url -> Promise<tone | null>

function toHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2, d = mx - mn;
  if (!d) return [0, 0, l];
  const s = d / (1 - Math.abs(2 * l - 1));
  const h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [(h * 60 + 360) % 360, s, l];
}

/**
 * Backdrop built from the product's own colour (same hue family), the way product shots are styled on beauty sites:
 * a soft gradient that is a shade deeper than a pale product, a shade lighter than a dark one, and about as vivid as
 * a vivid one, with a gentle highlight in the top-left corner for depth.
 */
export function backdropFor([h, s, l]) {
  const neutral = s < 0.12;                       // white, grey or black product: no hue of its own to borrow
  const hue = neutral ? (l > 0.5 ? 30 : 215) : h;
  const S = neutral ? (l > 0.5 ? 16 : 12) : Math.round(Math.min(0.9, Math.max(0.28, s * 1.05)) * 100);   // saturation in %
  let L;                                          // lightness of the middle of the gradient, in %
  if (l > 0.72) L = Math.max(62, l * 100 - 16);    // pale product -> deeper tone of its colour
  else if (l < 0.36) L = Math.min(84, l * 100 + 46); // dark product -> light tint of its colour
  else L = Math.min(80, l * 100 + 14);             // mid-tone product -> a lighter, softer tone
  const c = (d) => `hsl(${Math.round(hue)} ${S}% ${Math.max(18, Math.min(95, L + d)).toFixed(0)}%)`;
  return {
    solid: c(0),
    bg: `radial-gradient(120% 90% at 18% 8%, ${c(10)} 0%, rgba(255,255,255,0) 62%), linear-gradient(165deg, ${c(3)} 0%, ${c(-9)} 100%)`,
    dark: L < 62,
  };
}

function analyse(url) {
  return new Promise((resolve) => {
    if (/\.jpe?g(\?|#|$)/i.test(url)) return resolve(null);   // JPEG: never transparent, no need to download it again
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const W = 48, H = 48;
        const cv = document.createElement("canvas"); cv.width = W; cv.height = H;
        const cx = cv.getContext("2d", { willReadFrequently: true });
        cx.drawImage(img, 0, 0, W, H);
        const { data } = cx.getImageData(0, 0, W, H);
        let clear = 0, n = 0, R = 0, G = 0, B = 0;
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3];
          if (a < 200) clear += 1;
          else { const w = 0.3 + (Math.max(data[i], data[i + 1], data[i + 2]) - Math.min(data[i], data[i + 1], data[i + 2])) / 128; n += w; R += data[i] * w; G += data[i + 1] * w; B += data[i + 2] * w; }
        }
        const at = (x, y) => data[(y * W + x) * 4 + 3];
        const corners = [at(0, 0), at(W - 1, 0), at(0, H - 1), at(W - 1, H - 1)].filter((a) => a < 40).length;
        const transparent = n > 0 && (corners >= 3 || clear / (W * H) > 0.3);
        resolve(transparent ? backdropFor(toHsl(R / n, G / n, B / n)) : null);
      } catch { resolve(null); }       // cross-origin photo without CORS headers: leave it as is
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export function toneOf(url) {
  if (!url) return Promise.resolve(null);
  if (!cache.has(url)) cache.set(url, analyse(url));
  return cache.get(url);
}

/** The backdrop for this photo, or null when it is not transparent (or not known yet). */
export function useTone(url) {
  const [tone, setTone] = useState(null);
  useEffect(() => {
    let live = true;
    setTone(null);
    toneOf(url).then((t) => { if (live) setTone(t); });
    return () => { live = false; };
  }, [url]);
  return tone;
}
